import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DollarSignIcon, RepeatIcon, CalendarIcon, InfoIcon, CheckCircle2Icon, PlusIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { AnimatedScreen, staggerContainer, staggerItem } from '../ui/AnimatedScreen';
import { OPENFINANCE_THEME } from '../../constants/openFinanceTheme';
import { VRPFrequency, VRP_CONFIG, getVRPFrequencyText } from '../../constants/uaeStandards';
export interface ConnectedBank {
  id: string;
  name: string;
  logoUrl: string;
  accountType: string;
  accountLastFour: string;
  connectedDate: string;
}
export interface VRPSetupScreenProps {
  merchantName: string;
  merchantLogo?: string;
  billAmount: number;
  currency: string;
  connectedBanks: ConnectedBank[];
  onContinueWithBank: (config: VRPConfig, bankId: string) => void;
  onContinueWithNewBank: (config: VRPConfig) => void;
  onCancel?: () => void;
}
export interface VRPConfig {
  frequency: VRPFrequency;
  maxAmountPerPayment: number;
  totalPaymentsAllowed: number;
}
const FREQUENCY_OPTIONS: VRPFrequency[] = ['one-time', 'monthly', 'quarterly', 'annual'];
export function VRPSetupScreen({
  merchantName,
  merchantLogo,
  billAmount,
  currency,
  connectedBanks,
  onContinueWithBank,
  onContinueWithNewBank,
  onCancel
}: VRPSetupScreenProps) {
  const [frequency, setFrequency] = useState<VRPFrequency>('monthly');
  const [maxAmount, setMaxAmount] = useState(VRP_CONFIG.defaultMaxAmount);
  const [totalPayments, setTotalPayments] = useState(VRP_CONFIG.defaultMaxPayments);
  const [selectedBankId, setSelectedBankId] = useState<string | null>(null);
  const handleContinue = () => {
    const config: VRPConfig = {
      frequency,
      maxAmountPerPayment: maxAmount,
      totalPaymentsAllowed: totalPayments
    };
    if (selectedBankId) {
      // User selected existing bank - skip to authorization
      onContinueWithBank(config, selectedBankId);
    } else {
      // User wants to connect new bank - go to provider selection
      onContinueWithNewBank(config);
    }
  };
  const suggestedMaxAmount = Math.ceil(billAmount * 1.2); // 20% buffer
  const hasConnectedBanks = connectedBanks.length > 0;
  return <AnimatedScreen data-id="element-1378">
      <div className="space-y-6" data-id="element-1379">
        {/* Header */}
        <div data-id="element-1380">
          <h2 className="text-xl font-bold mb-2 leading-tight" style={{
          fontFamily: OPENFINANCE_THEME.fonts.display,
          color: OPENFINANCE_THEME.colors.primary700
        }} data-id="element-1381">
            Set Up Auto-Pay for {merchantName}
          </h2>
          <p className="text-sm leading-relaxed" style={{
          color: OPENFINANCE_THEME.colors.textLight,
          fontFamily: OPENFINANCE_THEME.fonts.body
        }} data-id="element-1382">
            Configure payment rules and choose your payment source
          </p>
        </div>

        {/* Merchant Card */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="p-4 border-2 rounded-2xl" style={{
        background: OPENFINANCE_THEME.gradients.card,
        borderColor: OPENFINANCE_THEME.colors.primary200
      }} data-id="element-1383">
          <div className="flex items-center gap-3" data-id="element-1384">
            {merchantLogo && <div className="w-12 h-12 rounded-xl flex items-center justify-center p-2" style={{
            backgroundColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1385">
                <img src={merchantLogo} alt={merchantName} className="w-full h-full object-contain" data-id="element-1386" />
              </div>}
            <div className="flex-1" data-id="element-1387">
              <div className="font-bold text-base" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1388">
                {merchantName}
              </div>
              <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1389">
                Typical bill: {currency} {billAmount.toLocaleString()}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Payment Frequency */}
        <motion.div variants={staggerContainer} initial="hidden" animate="show" data-id="element-1390">
          <div className="flex items-center gap-2 mb-3" data-id="element-1391">
            <RepeatIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1392" />
            <label className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1393">
              Payment Frequency
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-1394">
            {FREQUENCY_OPTIONS.map(freq => <motion.button key={freq} variants={staggerItem} onClick={() => setFrequency(freq)} className="p-4 rounded-xl border-2 transition-all text-left" style={{
            borderColor: frequency === freq ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border,
            backgroundColor: frequency === freq ? OPENFINANCE_THEME.colors.primary50 : 'white'
          }} data-id="element-1395">
                <div className="flex items-center justify-between mb-1" data-id="element-1396">
                  <span className="text-sm font-semibold" style={{
                color: frequency === freq ? OPENFINANCE_THEME.colors.primary700 : OPENFINANCE_THEME.colors.text
              }} data-id="element-1397">
                    {getVRPFrequencyText(freq)}
                  </span>
                  {frequency === freq && <CheckCircle2Icon className="w-5 h-5" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-1398" />}
                </div>
                <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1399">
                  {freq === 'one-time' && 'Single payment only'}
                  {freq === 'monthly' && 'Every month'}
                  {freq === 'quarterly' && 'Every 3 months'}
                  {freq === 'annual' && 'Once per year'}
                </div>
              </motion.button>)}
          </div>
        </motion.div>

        {/* Max Amount Per Payment */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2
      }} data-id="element-1400">
          <div className="flex items-center gap-2 mb-3" data-id="element-1401">
            <DollarSignIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.accent500
          }} data-id="element-1402" />
            <label className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1403">
              Maximum Amount Per Payment
            </label>
          </div>

          <div className="p-4 rounded-xl border-2" style={{
          borderColor: OPENFINANCE_THEME.colors.border,
          backgroundColor: 'white'
        }} data-id="element-1404">
            <div className="flex items-center gap-3 mb-3" data-id="element-1405">
              <span className="text-3xl font-bold" style={{
              color: OPENFINANCE_THEME.colors.text
            }} data-id="element-1406">
                {currency} {maxAmount.toLocaleString()}
              </span>
            </div>

            <input type="range" min={VRP_CONFIG.maxAmountLimits[0]} max={VRP_CONFIG.maxAmountLimits[VRP_CONFIG.maxAmountLimits.length - 1]} step={100} value={maxAmount} onChange={e => setMaxAmount(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
            background: `linear-gradient(to right, ${OPENFINANCE_THEME.colors.accent500} 0%, ${OPENFINANCE_THEME.colors.accent500} ${(maxAmount - VRP_CONFIG.maxAmountLimits[0]) / (VRP_CONFIG.maxAmountLimits[VRP_CONFIG.maxAmountLimits.length - 1] - VRP_CONFIG.maxAmountLimits[0]) * 100}%, ${OPENFINANCE_THEME.colors.borderLight} ${(maxAmount - VRP_CONFIG.maxAmountLimits[0]) / (VRP_CONFIG.maxAmountLimits[VRP_CONFIG.maxAmountLimits.length - 1] - VRP_CONFIG.maxAmountLimits[0]) * 100}%, ${OPENFINANCE_THEME.colors.borderLight} 100%)`
          }} data-id="element-1407" />

            <div className="flex justify-between mt-2" data-id="element-1408">
              {VRP_CONFIG.maxAmountLimits.map(amount => <button key={amount} onClick={() => setMaxAmount(amount)} className="text-xs font-medium px-2 py-1 rounded transition-colors" style={{
              color: maxAmount === amount ? OPENFINANCE_THEME.colors.accent500 : OPENFINANCE_THEME.colors.textMuted,
              backgroundColor: maxAmount === amount ? OPENFINANCE_THEME.colors.accent500 + '15' : 'transparent'
            }} data-id="element-1409">
                  {amount.toLocaleString()}
                </button>)}
            </div>

            {maxAmount < suggestedMaxAmount && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-3 p-3 rounded-lg flex items-start gap-2" style={{
            backgroundColor: OPENFINANCE_THEME.colors.warning + '15',
            border: `1px solid ${OPENFINANCE_THEME.colors.warning}30`
          }} data-id="element-1410">
                <InfoIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{
              color: OPENFINANCE_THEME.colors.warning
            }} data-id="element-1411" />
                <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.warning
            }} data-id="element-1412">
                  <span className="font-semibold" data-id="element-1413">
                    Suggested: {currency} {suggestedMaxAmount.toLocaleString()}
                  </span>
                  <br data-id="element-1414" />
                  Based on your typical bill amount plus 20% buffer for
                  variations.
                </div>
              </motion.div>}
          </div>
        </motion.div>

        {/* Total Payments Allowed */}
        {frequency !== 'one-time' && <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} data-id="element-1415">
            <div className="flex items-center gap-2 mb-3" data-id="element-1416">
              <CalendarIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.primary500
          }} data-id="element-1417" />
              <label className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1418">
                Total Payments Allowed
              </label>
            </div>

            <div className="grid grid-cols-3 gap-3" data-id="element-1419">
              {VRP_CONFIG.maxPaymentsLimits.filter(p => p > 1).map(payments => <button key={payments} onClick={() => setTotalPayments(payments)} className="p-4 rounded-xl border-2 transition-all" style={{
            borderColor: totalPayments === payments ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border,
            backgroundColor: totalPayments === payments ? OPENFINANCE_THEME.colors.primary50 : 'white'
          }} data-id="element-1420">
                    <div className="text-2xl font-bold mb-1" style={{
              color: totalPayments === payments ? OPENFINANCE_THEME.colors.primary700 : OPENFINANCE_THEME.colors.text
            }} data-id="element-1421">
                      {payments}
                    </div>
                    <div className="text-xs" style={{
              color: OPENFINANCE_THEME.colors.textMuted
            }} data-id="element-1422">
                      {frequency === 'monthly' && `${payments} months`}
                      {frequency === 'quarterly' && `${payments * 3} months`}
                      {frequency === 'annual' && `${payments} years`}
                    </div>
                  </button>)}
            </div>
          </motion.div>}

        {/* Divider */}
        <div className="relative" data-id="element-1423">
          <div className="absolute inset-0 flex items-center" aria-hidden="true" data-id="element-1424">
            <div className="w-full border-t-2" style={{
            borderColor: OPENFINANCE_THEME.colors.borderLight
          }} data-id="element-1425" />
          </div>
          <div className="relative flex justify-center" data-id="element-1426">
            <span className="px-3 text-xs font-semibold uppercase tracking-wide bg-gray-50" style={{
            color: OPENFINANCE_THEME.colors.textMuted
          }} data-id="element-1427">
              Payment Source
            </span>
          </div>
        </div>

        {/* Bank Selection Section */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} data-id="element-1428">
          {hasConnectedBanks && <div className="flex items-center gap-2 mb-3" data-id="element-1429">
              <ShieldCheckIcon className="w-5 h-5" style={{
            color: OPENFINANCE_THEME.colors.success
          }} data-id="element-1430" />
              <div className="text-sm font-bold uppercase tracking-wide" style={{
            color: OPENFINANCE_THEME.colors.text
          }} data-id="element-1431">
                Your Connected Banks
              </div>
            </div>}

          <div className="space-y-3" data-id="element-1432">
            {/* Connected Banks */}
            {connectedBanks.map(bank => <button key={bank.id} onClick={() => setSelectedBankId(bank.id)} className="w-full p-4 rounded-xl border-2 transition-all text-left" style={{
            borderColor: selectedBankId === bank.id ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.border,
            backgroundColor: selectedBankId === bank.id ? OPENFINANCE_THEME.colors.primary50 : 'white'
          }} data-id="element-1433">
                <div className="flex items-center gap-4" data-id="element-1434">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center p-2 flex-shrink-0" style={{
                backgroundColor: OPENFINANCE_THEME.colors.borderLight
              }} data-id="element-1435">
                    <img src={bank.logoUrl} alt={bank.name} className="w-full h-full object-contain" data-id="element-1436" />
                  </div>

                  <div className="flex-1 min-w-0" data-id="element-1437">
                    <div className="font-bold text-base mb-1" style={{
                  color: OPENFINANCE_THEME.colors.text
                }} data-id="element-1438">
                      {bank.name}
                    </div>
                    <div className="text-sm mb-1" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1439">
                      {bank.accountType}
                    </div>
                    <div className="text-xs font-mono" style={{
                  color: OPENFINANCE_THEME.colors.textMuted
                }} data-id="element-1440">
                      ****{bank.accountLastFour}
                    </div>
                  </div>

                  {selectedBankId === bank.id && <CheckCircle2Icon className="w-6 h-6" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-1441" />}
                </div>
              </button>)}

            {/* Connect New Bank */}
            <button onClick={() => setSelectedBankId(null)} className="w-full p-4 rounded-xl border-2 transition-all" style={{
            borderColor: selectedBankId === null ? OPENFINANCE_THEME.colors.primary500 : OPENFINANCE_THEME.colors.primary500,
            backgroundColor: selectedBankId === null ? OPENFINANCE_THEME.colors.primary50 : 'white',
            borderStyle: selectedBankId === null ? 'solid' : 'dashed'
          }} data-id="element-1442">
              <div className="flex items-center gap-4" data-id="element-1443">
                <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-1444">
                  <PlusIcon className="w-8 h-8 text-white" strokeWidth={3} data-id="element-1445" />
                </div>

                <div className="flex-1 text-left" data-id="element-1446">
                  <div className="font-bold text-base mb-1" style={{
                  color: OPENFINANCE_THEME.colors.primary700
                }} data-id="element-1447">
                    Connect a New Bank
                  </div>
                  <div className="text-sm" style={{
                  color: OPENFINANCE_THEME.colors.primary600
                }} data-id="element-1448">
                    {hasConnectedBanks ? 'Add another bank account' : 'Connect your first bank account'}
                  </div>
                </div>

                {selectedBankId === null && <CheckCircle2Icon className="w-6 h-6" style={{
                color: OPENFINANCE_THEME.colors.primary500
              }} data-id="element-1449" />}
              </div>
            </button>
          </div>
        </motion.div>

        {/* Actions */}
        <div className="space-y-3 pt-2" data-id="element-1450">
          {onCancel && <button onClick={onCancel} className="relative w-full px-6 py-4 bg-white font-semibold transition-all hover:bg-gray-50 overflow-hidden" style={{
          borderRadius: '50px'
        }} data-id="element-1451">
              <div className="absolute inset-0 rounded-full p-[2px]" style={{
            background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude'
          }} data-id="element-1452" />
              <span className="relative z-10" style={{
            background: 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} data-id="element-1453">
                Cancel
              </span>
            </button>}

          <button onClick={handleContinue} className="w-full px-6 py-4 text-white font-semibold transition-all hover:opacity-90" style={{
          background: 'linear-gradient(90deg, #00BCD4 0%, #1976D2 50%, #000000 100%)',
          borderRadius: '50px'
        }} data-id="element-1454">
            <div className="flex items-center justify-center gap-3" data-id="element-1455">
              <img src="https://cdn.magicpatterns.com/uploads/8Zyr2eTymP8hQsszbaoPhJ/White_Mark.svg" alt="ALTAREQ" className="h-6 w-6 object-contain" data-id="element-1456" />
              <span data-id="element-1457">Proceed using Al Tareq</span>
            </div>
          </button>
        </div>
      </div>
    </AnimatedScreen>;
}